import type { AxiosRequestConfig } from 'axios'

/**
 * Builds an Authorization header for call sites that use bare `axios` rather
 * than the shared `axiosInterceptor` instance.
 *
 * Several older slices call `axios.get(...)` directly, which sends no
 * credentials because the interceptor is applied to a separate axios instance
 * (see `services/axiosInterceptor.js`). That was invisible while the matching
 * API routes were unauthenticated; once those routes require a session, every
 * such call needs the token attached.
 *
 * Prefer `axiosInterceptor(API_BASE_URL)` for new code. This exists so the
 * existing bare-axios call sites can be corrected without restructuring them.
 */

/** Reads the persisted session token. */
export const getAuthToken = (): string | null => {
	try {
		const raw = localStorage.getItem('userInfo')
		if (!raw) return null
		const parsed = JSON.parse(raw) as { token?: string }
		return parsed?.token ?? null
	} catch {
		return null
	}
}

/**
 * Returns `{ Authorization: 'Bearer <token>' }`, or an empty object when there
 * is no session, so the caller can spread it unconditionally.
 */
export const authHeaders = (): Record<string, string> => {
	const token = getAuthToken()
	return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Merges auth headers into an axios request config, preserving any params and
 * headers the caller already set.
 *
 * Typed against `AxiosRequestConfig` rather than a hand-rolled generic. An
 * earlier version constrained the parameter to `{ headers?: ... }`, which is a
 * weak type — TypeScript rejects an argument that shares no properties with it,
 * so every `withAuth({ params: ... })` call failed to compile.
 */
export const withAuth = (config: AxiosRequestConfig = {}): AxiosRequestConfig => ({
	...config,
	headers: { ...(config.headers || {}), ...authHeaders() },
})
